import { UTCDate } from '@date-fns/utc';
import { Big } from 'big.js';
import { CategoryView, VendorView } from '../../../types/entity-types';
import { SupportedCountryCurrency } from '../../../types/supported-country-currency';
import { calculateMonthlyBreakdown, MonthlyAmountBreakdown } from './calculate-monthly-breakdown';
import { getCategoriesWithTransactionsInTimeframe } from './get-categories-with-transactions-in-timeframe';
import { getReportingTimeframe } from './get-reporting-timeframe';
import { getTransactionsInTimeframe } from './get-transactions-in-timeframe';
import { getVendorsWithTransactionsInTimeframe } from './get-vendors-with-transactions-in-timeframe';
import { hasVendorTransactionsInTimeframe } from './has-vendor-transactions-in-timeframe';

type HierarchyLevel = 'NO_CATEGORY' | 'LEVEL_1_CATEGORY' | 'LEVEL_2_CATEGORY' | 'VENDOR';

export type ReportingData = {
  hierarchyLevel: HierarchyLevel;
  id?: string;
  name: string;
  description?: string | null;
  logo?: string | null;
  icon?: string | null;
  amountBreakdown: MonthlyAmountBreakdown;
  transactionCount: number;
  vendorCount: number;
  children?: ReportingData[];
};

export type ReportingTotals = {
  data: ReportingData[];
  totals: MonthlyAmountBreakdown;
  vendors: VendorView[];
};

type GetReportingDataArgs = {
  categories: CategoryView[];
  vendors: VendorView[];
  baseCurrency: SupportedCountryCurrency;
};

export const getReportingData = ({ categories, vendors, baseCurrency }: GetReportingDataArgs): ReportingTotals => {
  const { startReportingDate, endReportingDate } = getReportingTimeframe({ today: new UTCDate() });

  const vendorsWithTransactionsInLast4Months = getVendorsWithTransactionsInTimeframe({
    vendors,
    startDate: startReportingDate,
    endDate: endReportingDate,
  });
  const categoriesWithVendorsInLast4Months = getCategoriesWithTransactionsInTimeframe({
    categories,
    startDate: startReportingDate,
    endDate: endReportingDate,
  });

  // Get uncategorized vendors (those without categoryId)
  const uncategorizedVendors = vendorsWithTransactionsInLast4Months.filter((vendor) => !vendor.categoryId);

  const uncategorizedVendorsWithTimeframeTransactions = uncategorizedVendors.map((v) => ({
    ...v,
    transactions: getTransactionsInTimeframe({
      transactions: v.transactions ?? [],
      startDate: startReportingDate,
      endDate: endReportingDate,
    }),
  }));

  const uncategorizedData: ReportingData = {
    hierarchyLevel: 'NO_CATEGORY',
    name: 'Uncategorized',
    amountBreakdown: calculateMonthlyBreakdown(
      uncategorizedVendorsWithTimeframeTransactions.flatMap((vendor) => vendor.transactions ?? []),
      baseCurrency,
    ),
    transactionCount: uncategorizedVendorsWithTimeframeTransactions.reduce(
      (sum, vendor) => sum + (vendor.transactions?.length ?? 0),
      0,
    ),
    vendorCount: uncategorizedVendorsWithTimeframeTransactions.length,
    children: [
      ...uncategorizedVendorsWithTimeframeTransactions.map((vendor) => ({
        hierarchyLevel: 'VENDOR' as const,
        id: vendor.id,
        name: vendor.name,
        description: vendor.description,
        logo: vendor.logo,
        amountBreakdown: calculateMonthlyBreakdown(vendor.transactions, baseCurrency),
        transactionCount: vendor.transactions?.length ?? 0,
        vendorCount: 1,
      })),
    ].sort((a, b) => b.amountBreakdown.previousMonthAmount - a.amountBreakdown.previousMonthAmount),
  };

  const categorizedVendors: VendorView[] = [];

  // Get unique level1 category names
  const uniqueLevel1NameList = [...new Set(categoriesWithVendorsInLast4Months.map((cat) => cat.level1))].filter(
    Boolean,
  );

  const categorizedData: ReportingData[] = uniqueLevel1NameList.map((level1Name) => {
    // Get all categories for this level1 that have a level2
    const filteredCategoryList = categoriesWithVendorsInLast4Months.filter(
      (cat) => cat.level1 === level1Name && cat.level2,
    );

    const levelTwo: ReportingData[] = filteredCategoryList.map((category) => {
      // Get all categories for this level2
      const categoryListLevel2 = filteredCategoryList.filter((cat) => cat.level2 === category.level2);

      // Get all vendors for this level2
      const vendorListLevel2 = categoryListLevel2.flatMap((cat) => cat.vendors || []);

      categorizedVendors.push(...vendorListLevel2);

      // Calculate vendor summaries
      const vendorSummaryList = vendorListLevel2
        // Filters vendors that have no transactions between start and end reporting date
        .filter((v) =>
          hasVendorTransactionsInTimeframe({ vendor: v, startDate: startReportingDate, endDate: endReportingDate }),
        )
        .map((v) => ({
          ...v,
          // Filters transactions that are within the reporting date range
          transactions: getTransactionsInTimeframe({
            transactions: v.transactions ?? [],
            startDate: startReportingDate,
            endDate: endReportingDate,
          }),
        }))
        .map<ReportingData>((vendor) => ({
          hierarchyLevel: 'VENDOR',
          id: vendor.id,
          name: vendor.name,
          description: vendor.description,
          logo: vendor.logo,
          amountBreakdown: calculateMonthlyBreakdown(vendor.transactions, baseCurrency),
          transactionCount: vendor.transactions?.length ?? 0,
          vendorCount: 1,
        }));

      return {
        hierarchyLevel: 'LEVEL_2_CATEGORY',
        id: category.id,
        name: category.level2,
        icon: category.icon,
        description: category.description,
        amountBreakdown: vendorSummaryList.reduce(
          (sum, vendor) => {
            const test = {
              currency: baseCurrency,
              totalAmount: new Big(vendor.amountBreakdown.totalAmount + sum.totalAmount),
              currentMonthAmount: new Big(vendor.amountBreakdown.currentMonthAmount + sum.currentMonthAmount),
              previousMonthAmount: new Big(vendor.amountBreakdown.previousMonthAmount + sum.previousMonthAmount),
              twoMonthsAgoAmount: new Big(vendor.amountBreakdown.twoMonthsAgoAmount + sum.twoMonthsAgoAmount),
              threeMonthsAgoAmount: new Big(vendor.amountBreakdown.threeMonthsAgoAmount + sum.threeMonthsAgoAmount),
              monthOverMonthIncrease: new Big(
                vendor.amountBreakdown.monthOverMonthIncrease + sum.monthOverMonthIncrease,
              ),
            };

            return {
              currency: baseCurrency,
              totalAmount: Number(test.totalAmount),
              currentMonthAmount: Number(test.currentMonthAmount),
              previousMonthAmount: Number(test.previousMonthAmount),
              twoMonthsAgoAmount: Number(test.twoMonthsAgoAmount),
              threeMonthsAgoAmount: Number(test.threeMonthsAgoAmount),
              monthOverMonthIncrease: Number(test.monthOverMonthIncrease),
            };
          },
          {
            currency: baseCurrency,
            totalAmount: 0,
            currentMonthAmount: 0,
            previousMonthAmount: 0,
            twoMonthsAgoAmount: 0,
            threeMonthsAgoAmount: 0,
            monthOverMonthIncrease: 0,
          },
        ),
        transactionCount: vendorSummaryList.reduce((sum, vendor) => sum + vendor.transactionCount, 0),
        vendorCount: vendorSummaryList.length,
        children: [...vendorSummaryList].sort(
          (a, b) => a.amountBreakdown.previousMonthAmount - b.amountBreakdown.previousMonthAmount,
        ),
      };
    });

    return {
      hierarchyLevel: 'LEVEL_1_CATEGORY',
      name: level1Name,
      amountBreakdown: levelTwo.reduce(
        (sum, l2) => {
          const test = {
            currency: baseCurrency,
            totalAmount: new Big(l2.amountBreakdown.totalAmount).plus(sum.totalAmount),
            currentMonthAmount: new Big(l2.amountBreakdown.currentMonthAmount).plus(sum.currentMonthAmount),
            previousMonthAmount: new Big(l2.amountBreakdown.previousMonthAmount).plus(sum.previousMonthAmount),
            twoMonthsAgoAmount: new Big(l2.amountBreakdown.twoMonthsAgoAmount).plus(sum.twoMonthsAgoAmount),
            threeMonthsAgoAmount: new Big(l2.amountBreakdown.threeMonthsAgoAmount).plus(sum.threeMonthsAgoAmount),
            monthOverMonthIncrease: new Big(l2.amountBreakdown.monthOverMonthIncrease).plus(sum.monthOverMonthIncrease),
          };

          return {
            currency: baseCurrency,
            totalAmount: Number(test.totalAmount),
            currentMonthAmount: Number(test.currentMonthAmount),
            previousMonthAmount: Number(test.previousMonthAmount),
            twoMonthsAgoAmount: Number(test.twoMonthsAgoAmount),
            threeMonthsAgoAmount: Number(test.threeMonthsAgoAmount),
            monthOverMonthIncrease: Number(test.monthOverMonthIncrease),
          };
        },
        {
          currency: baseCurrency,
          totalAmount: 0,
          currentMonthAmount: 0,
          previousMonthAmount: 0,
          twoMonthsAgoAmount: 0,
          threeMonthsAgoAmount: 0,
          monthOverMonthIncrease: 0,
        },
      ),
      transactionCount: levelTwo.reduce((sum, l2) => sum + l2.transactionCount, 0),
      vendorCount: levelTwo.reduce((sum, l2) => sum + l2.vendorCount, 0),
      children: levelTwo,
    };
  });

  // Uncategorized data goes last
  // https://linear.app/seapoint/issue/TENG-734/uncategorized-section-should-be-last
  const reportingData = [...categorizedData, ...(uncategorizedData.children?.length ? [uncategorizedData] : [])];

  // Calculate totals across all items
  const totals = reportingData.reduce(
    (sum, item) => {
      const test = {
        currency: baseCurrency,
        totalAmount: new Big(sum.totalAmount).plus(item.amountBreakdown.totalAmount),
        currentMonthAmount: new Big(sum.currentMonthAmount).plus(item.amountBreakdown.currentMonthAmount),
        previousMonthAmount: new Big(sum.previousMonthAmount).plus(item.amountBreakdown.previousMonthAmount),
        twoMonthsAgoAmount: new Big(sum.twoMonthsAgoAmount).plus(item.amountBreakdown.twoMonthsAgoAmount),
        threeMonthsAgoAmount: new Big(sum.threeMonthsAgoAmount).plus(item.amountBreakdown.threeMonthsAgoAmount),
        monthOverMonthIncrease: new Big(sum.monthOverMonthIncrease).plus(item.amountBreakdown.monthOverMonthIncrease),
      };

      return {
        currency: baseCurrency,
        totalAmount: Number(test.totalAmount),
        currentMonthAmount: Number(test.currentMonthAmount),
        previousMonthAmount: Number(test.previousMonthAmount),
        twoMonthsAgoAmount: Number(test.twoMonthsAgoAmount),
        threeMonthsAgoAmount: Number(test.threeMonthsAgoAmount),
        monthOverMonthIncrease: Number(test.monthOverMonthIncrease),
      };
    },
    {
      currency: baseCurrency,
      totalAmount: 0,
      currentMonthAmount: 0,
      previousMonthAmount: 0,
      twoMonthsAgoAmount: 0,
      threeMonthsAgoAmount: 0,
      monthOverMonthIncrease: 0,
    },
  );

  return {
    data: reportingData,
    totals,
    // Needed so the user can iterate in the vendor modal over the same order of vendors as in the table
    // Uncategorized data goes last
    // https://linear.app/seapoint/issue/TENG-734/uncategorized-section-should-be-last
    // TODO: https://linear.app/seapoint/issue/TENG-1252/confusing-reporting-numbers#comment-331718f4
    vendors: [...categorizedVendors, ...uncategorizedVendors],
  };
};
